import React from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip } from 'antd';
import { LockFilled } from '@ant-design/icons';
import { Trans } from 'react-i18next';
import IframeServiceInjector from '../services/injectors/IframeServiceInjector';
import EditorStateMachine from '../model/EditorStateMachine';

interface LockedElementOverlayProps {
  label: string;
  elementPosition: DOMRect;
  iframeElement: HTMLIFrameElement;
  uniqueKey: string; // Unique key for registering with WrapperSynchronizer
  elementType: string; // Type of locked element (e.g., 'locked-navigation', 'locked-logo')
}

const LockedElementOverlay: React.FC<LockedElementOverlayProps> = observer(
  ({ label, elementPosition, iframeElement, uniqueKey, elementType }) => {
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const wrapperRef = React.useRef<any>(null);
    const iframeRect = iframeElement.getBoundingClientRect();

    // Check if this locked element is currently hovered (from EditorStateMachine)
    const isHovered = EditorStateMachine.hoveredLockedElementType === elementType;

    // Convert iframe-relative position to absolute document position
    let top = elementPosition.top + iframeRect.top;
    let left = elementPosition.left + iframeRect.left;
    let height = elementPosition.height;
    let width = elementPosition.width;

    // Clip if element is outside viewport (top)
    if (top < iframeRect.top) {
      height = height - (iframeRect.top - top);
      top = iframeRect.top;
    }

    // Clip if element is outside viewport (bottom)
    const bottom = top + height;
    const iframeBottom = iframeRect.top + iframeRect.height;
    if (bottom > iframeBottom) {
      height = height - (bottom - iframeBottom);
    }

    // Clip if element is outside viewport (left)
    if (left < iframeRect.left) {
      width = width - (iframeRect.left - left);
      left = iframeRect.left;
    }

    // Clip if element is outside viewport (right)
    const right = left + width;
    const iframeRight = iframeRect.left + iframeRect.width;
    if (right > iframeRight) {
      width = width - (right - iframeRight);
    }

    // Hide if completely outside iframe bounds
    if (height <= 0 || width <= 0) {
      return null;
    }

    // Custom tooltip per element type
    let tooltipText: React.ReactNode = label;
    if (label === 'Navigation') {
      tooltipText = (
        <Trans i18nKey="lockedNavigationTooltip" components={{ b: <b /> }}>
          The navigation isn't available in the preview. Use the <b>"Pages"</b> section in the left navigation.
        </Trans>
      );
    } else if (label === 'Logo') {
      tooltipText = (
        <Trans i18nKey="lockedLogoTooltip" components={{ b: <b /> }}>
          The logo cannot be changed in the preview. Use the <b>"Logo"</b> section in the left navigation.
        </Trans>
      );
    } else if (label === 'FAQ') {
      tooltipText = (
        <Trans i18nKey="lockedFaqTooltip" components={{ b: <b /> }}>
          The FAQs can’t be edited in the preview. You can <b>change</b>, <b>add</b> and <b>delete</b> FAQs in our <b>CMS</b> after <b>purchasing</b> the product.
        </Trans>
      );
    } else if (label === 'Contact Form') {
      tooltipText = (
        <Trans i18nKey="lockedContactFormTooltip" components={{ b: <b /> }}>
          The form cannot be edited in the preview. You can <b>change</b>, <b>add</b> and <b>delete</b> forms in our CMS after purchasing the product.
        </Trans>
      );
    }

    return (
      <div
        ref={(ref) => {
          overlayRef.current = ref;
          
          const WrapperSynchronizer = IframeServiceInjector.getService<any>(
            'WrapperSynchronizer'
          );

          if (ref === null) {
            if (wrapperRef.current) {
              WrapperSynchronizer.unregisterElement(`locked-${uniqueKey}`);
              wrapperRef.current = null;
            }
            return;
          }

          // Create wrapper class to handle position updates on scroll
          class LockedElementWrapper {
            constructor(
              private htmlElement: HTMLElement,
              private iframeElement: HTMLIFrameElement
            ) {}

            updateElementPosition(pos: any, iframeRect: any): void {
              // Get top position relative to the iframe element
              let top = pos.top + iframeRect.top;
              let left = pos.left + iframeRect.left;
              let height = pos.height;
              let width = pos.width;

              // Clip if element is outside viewport (top)
              if (top < iframeRect.top) {
                height = height - (iframeRect.top - top);
                top = iframeRect.top;
              }

              // Clip if element is outside viewport (bottom)
              const bottom = top + height;
              const iframeBottom = iframeRect.top + iframeRect.height;
              if (bottom > iframeBottom) {
                height = height - (bottom - iframeBottom);
              }

              // Clip if element is outside viewport (left)
              if (left < iframeRect.left) {
                width = width - (iframeRect.left - left);
                left = iframeRect.left;
              }

              // Clip if element is outside viewport (right)
              const right = left + width;
              const iframeRight = iframeRect.left + iframeRect.width;
              if (right > iframeRight) {
                width = width - (right - iframeRight);
              }
              if (height <= 0 || width <= 0) {
                this.htmlElement.style.display = 'none';
              } else {
                this.htmlElement.style.display = 'flex';
                this.htmlElement.style.top = top + 'px';
                this.htmlElement.style.left = left + 'px';
                this.htmlElement.style.width = width + 'px';
                this.htmlElement.style.height = height + 'px';
              }
            }
          }

          wrapperRef.current = new LockedElementWrapper(ref, iframeElement);
          const wrapperId = `locked-${uniqueKey}`;
          WrapperSynchronizer.registerElement(
            { element: wrapperRef.current },
            wrapperId
          );
        }}
        className="locked-element-overlay fixed flex items-center justify-center border border-dashed border-[#125590] bg-[#D9D9D980]/50 pointer-events-none z-[1000]"
        style={{
          top: `${top}px`,
          left: `${left}px`,
          width: `${width}px`,
          height: `${height}px`,
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.2s',
        }}
      >
        <div className="pointer-events-auto">
          <Tooltip
            title={
              <div
                className="px-2 py-1.5 bg-[#000C17] text-white font-roboto font-normal text-[14px] leading-[22px] text-left rounded max-w-[320px]"
                style={{fontFamily: 'Roboto, sans-serif'}}
              >
                {tooltipText}
              </div>
            }
            placement="top"
            overlayInnerStyle={{
              background: 'transparent',
              boxShadow: 'none',
              padding: 0,
            }}
          >
            <LockFilled className="text-3xl text-[#00000073]" />
          </Tooltip>
        </div>
      </div>
    );
  }
);

export default LockedElementOverlay;
